import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "#/components/shared/LegalPage";
import { BUSINESS } from "#/lib/constants";

const TITLE = "Privacy Policy | Blue Rock Concrete";
const DESCRIPTION =
  "How Blue Rock Concrete collects, uses, and protects the information you submit through our estimate and contact forms.";
const URL = "https://bluerockconcreteco.com/privacy";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" updated="July 26, 2026">
      <p>
        <strong>{BUSINESS.name}</strong> ("Company," "we," "us," or "our") is
        committed to protecting the privacy of visitors to{" "}
        <strong>bluerockconcreteco.com</strong> (the "Website"). This Privacy
        Policy explains what information we collect, how we use it, how we
        protect it, and what rights you have over it.
      </p>
      <p>
        By using the Website you agree to the collection and use of information
        as described here. If you do not agree, please do not use the Website.
        Read this alongside our <a href="/terms">Terms of Service</a>.
      </p>

      <h2>1. Information We Collect</h2>
      <p>
        We collect only what you choose to send us through the forms on this
        Website. We do not collect information passively, we do not use
        advertising or tracking cookies, and we do not buy contact data from
        third parties.
      </p>

      <h3>a. Free Estimate Form</h3>
      <p>
        The estimate form appears on the home page, on <a href="/quote">/quote</a>,
        and alongside our service pages. When you submit it we receive:
      </p>
      <ul>
        <li>Your name</li>
        <li>Phone number</li>
        <li>Email address</li>
        <li>The service you are interested in</li>
        <li>Property type, residential or commercial (optional)</li>
        <li>Project address (optional)</li>
        <li>Project details you write in the message box (optional)</li>
      </ul>

      <h3>b. Contact Form</h3>
      <p>
        When you send a general inquiry from <a href="/contact">/contact</a> we
        receive:
      </p>
      <ul>
        <li>Your name</li>
        <li>Email address</li>
        <li>Phone number (optional)</li>
        <li>Reason for contacting us</li>
        <li>Your message</li>
      </ul>

      <h3>c. What We Do Not Collect</h3>
      <p>We do not collect, store, or process:</p>
      <ul>
        <li>Payment or financial details. We never take card or bank information through this Website.</li>
        <li>Social Security numbers or government identification</li>
        <li>Precise device location. We do not track where you are.</li>
        <li>
          Information from children. This Website is intended for adults and is
          not directed at anyone under 18.
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>What you send us is used only to do the job you contacted us about:</p>
      <ul>
        <li>
          <strong>Responding to you.</strong> We use your name, phone, and email
          to reply to your request.
        </li>
        <li>
          <strong>Preparing an estimate.</strong> We use the service, property
          type, address, and project details to scope and price the work, and to
          find the site for an on-site measurement.
        </li>
        <li>
          <strong>Scheduling and performing work</strong> you hire us to do.
        </li>
        <li>
          <strong>Keeping records</strong> of quotes and completed projects, and
          honoring our workmanship warranty.
        </li>
      </ul>
      <p>
        We do not sell, rent, or trade your personal information, and we do not
        use it to send marketing you did not ask for.
      </p>

      <h2>3. How We Share Your Information</h2>
      <p>
        We share information only where it is needed to run the Website or
        deliver the work:
      </p>
      <ul>
        <li>
          <strong>Email delivery.</strong> Form submissions are delivered to our
          inbox through Resend, an email service provider. They process the
          message on our behalf and do not use it for their own purposes.
        </li>
        <li>
          <strong>Website hosting.</strong> The Website runs on Cloudflare, which
          serves the pages and provides standard security and abuse protection.
        </li>
        <li>
          <strong>Subcontractors and suppliers</strong> working on your project,
          and only the details they need to do it.
        </li>
        <li>
          <strong>Legal requirements.</strong> We may disclose information if the
          law requires it, or to establish or defend a legal claim.
        </li>
      </ul>

      <h2>4. Data Storage and Security</h2>
      <p>
        Submissions arrive as email and are stored in our business email account.
        The Website is served over HTTPS, so what you type in a form is
        encrypted in transit. Access is limited to the people who need it to
        respond to you.
      </p>
      <p>
        No method of transmission or storage is completely secure. We protect
        your information with reasonable safeguards, but we cannot guarantee
        absolute security, and you send information to us at your own risk.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We keep estimate and inquiry records for as long as we need them for the
        purpose you sent them, and afterward only as long as our tax, insurance,
        and warranty obligations require. You can ask us to delete your
        information sooner, and we will unless we are required to keep it.
      </p>

      <h2>6. Cookies and Tracking</h2>
      <p>
        This Website does not use advertising cookies, analytics profiling, or
        cross-site trackers. Your browser may store what it needs to display the
        site correctly, and our host may set strictly necessary cookies for
        security and performance. None of that identifies you personally or
        follows you to other websites.
      </p>

      <h2>7. Third-Party Links</h2>
      <p>
        The Website links to other places, such as our social profiles and our
        Google Business Profile. Once you follow a link you are on their site,
        under their privacy policy, not ours. We are not responsible for how
        they handle your information.
      </p>

      <h2>8. Your Rights</h2>
      <p>
        Contact us using the details in section 11 and you can ask us to:
      </p>
      <ul>
        <li>Tell you what information about you we hold</li>
        <li>Correct anything that is wrong</li>
        <li>Delete your information, subject to our record-keeping obligations</li>
        <li>Stop contacting you</li>
      </ul>
      <p>
        We will respond within a reasonable time and will not charge you or treat
        you differently for asking.
      </p>

      <h2>9. Children's Privacy</h2>
      <p>
        We do not knowingly collect information from anyone under 18. If you
        believe a child has sent us personal information, contact us and we will
        delete it.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy as our practices or the law change.
        The revised version takes effect when posted, and the date at the top of
        this page always shows when it last changed. Significant changes will be
        noted on this page.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        Questions about this policy, or about information you have sent us:
      </p>
      <ul>
        <li>
          Email: <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
        </li>
        <li>
          Phone: <a href={BUSINESS.phoneHref}>{BUSINESS.phone}</a>
        </li>
        <li>{BUSINESS.name}, {BUSINESS.address}</li>
      </ul>
    </LegalPage>
  );
}
